import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { PackageService } from '../../services/package.service';
import { ProfileService } from '../../services/profile.service';
import { AppConfigService } from '../../services/app-config.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { Router } from '@angular/router';
import { sanitizeErrorMessage } from '../../shared/utils/error.util';
import { sanitizePriceInputValue } from '../../shared/utils/price-input.util';
import { FAMILY_PLAN_FEATURE, isFeatureOn } from '../../shared/utils/app-feature.util';
import { buildFamilyEligible } from '../../shared/utils/package-family.util';
import { ThemeColorPickerComponent } from '../../shared/components/theme-color-picker';
import { ModalFooterActionsComponent } from '../../shared/components/modal-footer-actions/modal-footer-actions.component';
import { DEFAULT_THEME_BACKGROUND } from '../../shared/utils/theme-color.util';
import { mustContainAlphanumericValidator } from '../../shared/validators/display-name.validator';
import { OrderUtils } from '../../orders/utils/order.utils';
import type { PackagePayload } from '../../shared/types';
import type { LocationTunnelWashPackages } from '../../shared/types/package-tunnel-wash.types';
import {
  TUNNEL_WASH_FIELD_HINT,
  TunnelWashField,
  buildTunnelPackagesPayload,
  buildTunnelWashFields,
  isTunnelWashEligiblePackage,
  resolveTunnelWashSelection,
  tunnelSelectionsFromPackage,
  tunnelWashLocationIds,
} from '../../shared/utils/package-tunnel-wash.util';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-add-package-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatRadioModule,
    MatCheckboxModule,
    MatSelectModule,
    ReactiveFormsModule,
    ThemeColorPickerComponent,
    ModalFooterActionsComponent,
  ],
  templateUrl: './add-package-modal.component.html',
  styleUrls: ['./add-package-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AddPackageModalComponent implements OnInit, OnDestroy {
  @Input() packageData: any = null;
  @Output() close = new EventEmitter<void>();

  packageForm: FormGroup;
  locations: any[] = [];
  features: any[] = [];
  submitted = false; // Add submitted flag
  hasFormChanges = false; // Track if form has changes
  showAllFeatures = false; // Track whether to show all features or just first 10
  searchTerm = ''; // Track search input for filtering features
  isSubmitting = false; // Track if form is being submitted
  /** CWS-1805: family plan section is shown only when the super-admin flag is on for this company. */
  familyPlanFeatureOn = false;
  washCountPeriodOptions = [
    { label: 'Per month', value: 'per_month' },
    { label: 'Unlimited', value: 'unlimited' },
  ];
  /** Add-on: `paired_with` values sent to the API. */
  pairedWithOptions: { value: string; label: string }[] = [
    { value: 'single_wash', label: 'Single Wash' },
    { value: 'monthly_membership', label: 'Monthly Membership' },
    { value: 'prepaid', label: 'Prepaid' },
    { value: 'individual', label: 'Individual' },
  ];
  /** Car wash "Type of Wash" checkbox values (excludes detailing/add_on API tokens). */
  private static readonly CAR_WASH_WASH_TYPES = ['single_wash', 'monthly_membership'] as const;
  /** Add-on variants: tracks which variant rows are collapsed (saved) vs. open (editing). */
  variantSavedStates: boolean[] = [];
  /** Add-on variants: tracks which variant kebab menus are open. */
  variantKebabOpen: boolean[] = [];

  /**
   * CWS-1921: "Tunnel wash package" dropdowns, one per selected location whose tunnel
   * has wash packages (none for shops without tunnels). Choices live outside the form
   * so the existing payload is untouched; `tunnelSelections` maps location id → tunnel
   * package id, null = Manual.
   */
  tunnelWashFields: TunnelWashField[] = [];
  tunnelSelections: Record<string, string | null> = {};
  readonly tunnelWashHint = TUNNEL_WASH_FIELD_HINT;
  private tunnelWashOptions: LocationTunnelWashPackages[] = [];
  private tunnelWashOptionsKey = '';
  private tunnelWashOptionsSub?: Subscription;

  // Custom validator for maximum value (999999.99 for list prices)
  maxListPriceValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || control.value === '') {
        return null; // Don't validate empty values (use required validator for that)
      }

      const numericValue = parseFloat(control.value.toString().replace(/[^0-9.]/g, ''));
      if (!isNaN(numericValue) && numericValue > 999999.99) {
        return { maxDigits: { maxDigits: 999999.99, actualValue: numericValue } };
      }

      return null;
    };
  }

  // Custom validator for maximum digits (for non-price fields)
  maxDigitsValidator(maxDigits: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || control.value === '') {
        return null; // Don't validate empty values (use required validator for that)
      }

      const value = control.value.toString().replace(/[^\d]/g, ''); // Remove all non-digit characters

      if (value.length > maxDigits) {
        return { maxDigits: { maxDigits, actualDigits: value.length } };
      }

      return null;
    };
  }

  // Custom validator to check if strike-off price is greater than list price
  strikeoffPriceGreaterThanListPriceValidator(listPriceControlName: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      try {
        if (this.isEmptyStrikeoffPrice(control?.value)) {
          return null; // Strike-off price is optional
        }

        const parent = control.parent;
        if (!parent) {
          return null;
        }

        // Get strike-off price value (remove $ and convert to number)
        const strikeoffValue = parseFloat(control.value.toString().replace(/[$%]/g, ''));
        if (isNaN(strikeoffValue)) {
          return null; // Invalid number, let other validators handle it
        }

        // Get list price value
        const listPriceControl = parent.get(listPriceControlName);
        if (
          !listPriceControl ||
          !listPriceControl.value ||
          listPriceControl.value === '' ||
          listPriceControl.value === null ||
          listPriceControl.value === undefined
        ) {
          return null; // If list price is not set, skip validation
        }

        const listPriceValue = parseFloat(listPriceControl.value.toString().replace(/[$%]/g, ''));
        if (isNaN(listPriceValue) || listPriceValue <= 0) {
          return null; // Invalid list price, skip validation
        }

        // Check if strike-off price is greater than list price
        if (strikeoffValue <= listPriceValue) {
          return {
            strikeoffPriceNotGreaterThanListPrice: {
              strikeoffPrice: strikeoffValue,
              listPrice: listPriceValue,
            },
          };
        }

        return null;
      } catch (error) {
        // If any error occurs during validation, return null to prevent breaking the form
        console.warn('Error in strikeoffPriceGreaterThanListPriceValidator:', error);
        return null;
      }
    };
  }

  // Custom validator to check if list price is less than strike-off price (when strike-off price exists)
  listPriceLessThanStrikeoffPriceValidator(strikeoffPriceControlName: string) {
    return (control: AbstractControl): ValidationErrors | null => {
      try {
        if (
          !control ||
          !control.value ||
          control.value === '' ||
          control.value === null ||
          control.value === undefined
        ) {
          return null; // Don't validate empty values (use required validator for that)
        }

        const parent = control.parent;
        if (!parent) {
          return null;
        }

        // Get list price value (remove $ and convert to number)
        const listPriceValue = parseFloat(control.value.toString().replace(/[$%]/g, ''));
        if (isNaN(listPriceValue) || listPriceValue <= 0) {
          return null; // Invalid number, let other validators handle it
        }

        // Get strike-off price value
        const strikeoffPriceControl = parent.get(strikeoffPriceControlName);
        if (!strikeoffPriceControl || this.isEmptyStrikeoffPrice(strikeoffPriceControl.value)) {
          return null; // If strike-off price is not set, skip validation
        }

        const strikeoffValue = parseFloat(
          strikeoffPriceControl.value.toString().replace(/[$%]/g, '')
        );
        if (isNaN(strikeoffValue)) {
          return null;
        }

        // Check if list price is less than strike-off price
        if (listPriceValue >= strikeoffValue) {
          return {
            listPriceMustBeLessThanStrikeoffPrice: {
              listPrice: listPriceValue,
              strikeoffPrice: strikeoffValue,
            },
          };
        }

        return null;
      } catch (error) {
        // If any error occurs during validation, return null to prevent breaking the form
        console.warn('Error in listPriceLessThanStrikeoffPriceValidator:', error);
        return null;
      }
    };
  }

  /** API / PrimeNG may send 1 or "true"; treat like boolean true. */
  private isAllowsCustomPriceValue(v: any): boolean {
    return v === true || v === 1 || v === '1' || v === 'true';
  }

  /** Strike-off is optional; API may return `0` / `0.00` when none was set. */
  private isEmptyStrikeoffPrice(raw: any): boolean {
    if (raw === '' || raw === null || raw === undefined) {
      return true;
    }
    const n = parseFloat(String(raw).replace(/[$,]/g, ''));
    return !Number.isFinite(n) || n <= 0;
  }

  private normalizeStrikeoffPriceForForm(raw: any): string {
    return this.isEmptyStrikeoffPrice(raw) ? '' : String(raw).trim();
  }

  /** Add-on variant row: list price required when not using custom price. */
  private addOnVariantRowValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const g = group as FormGroup;
    if (!g) {
      return null;
    }
    if (this.isAllowsCustomPriceValue(g.get('allows_custom_price')?.value)) {
      return null;
    }
    const list = g.get('single_list_price')?.value;
    if (list === '' || list === null || list === undefined) {
      return { listRequired: true };
    }
    const n = parseFloat(String(list).replace(/[^0-9.]/g, ''));
    if (isNaN(n) || n < 0) {
      return { listRequired: true };
    }
    return null;
  };

  /**
   * Add-on variant row: fixed pricing keeps list/strike cross-validators; custom pricing must not
   * (e.g. strike `0` triggers {@link strikeoffPriceGreaterThanListPriceValidator}'s zero error and disables Save).
   */
  private syncAddOnVariantListStrikeValidators(g: FormGroup): void {
    const list = g.get('single_list_price');
    const strike = g.get('single_strikeoff_price');
    if (this.isAllowsCustomPriceValue(g.get('allows_custom_price')?.value)) {
      list?.setValidators([this.maxListPriceValidator()]);
      strike?.setValidators([this.maxListPriceValidator()]);
    } else {
      list?.setValidators([
        this.maxListPriceValidator(),
        this.listPriceLessThanStrikeoffPriceValidator('single_strikeoff_price'),
      ]);
      strike?.setValidators([
        this.maxListPriceValidator(),
        this.strikeoffPriceGreaterThanListPriceValidator('single_list_price'),
      ]);
    }
    list?.updateValueAndValidity({ emitEvent: false });
    strike?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Reads variant rows from the FormArray so list / strike values are always included
   * (getRawValue() on the root form can omit or snapshot stale nested values for add-ons).
   */
  private getAddOnVariantRowsForSave(): any[] {
    if (!this.variants?.length) {
      return [];
    }
    return this.variants.controls.map(c => (c as FormGroup).getRawValue() as any);
  }

  private parseAddOnPriceField(raw: any): { str: string; num: number } {
    if (raw === null || raw === undefined) {
      return { str: '', num: NaN };
    }
    const s = String(raw).trim();
    if (s === '') {
      return { str: '', num: NaN };
    }
    const num = parseFloat(s.replace(/[$,]/g, ''));
    return { str: s, num: Number.isFinite(num) ? num : NaN };
  }

  /** Maps one variant form row to the add-on API payload object. */
  private mapAddOnVariantRowToPayload(row: any): any {
    const allowsCustom = this.isAllowsCustomPriceValue(row.allows_custom_price);
    const v: any = {
      variant_name: row.variant_name,
      quantity_selection: row.quantity_selection,
      allows_custom_price: allowsCustom,
      is_active: row.is_active !== false,
    };
    const { str: listStr, num: listNum } = this.parseAddOnPriceField(row.single_list_price);
    const { str: strikeStr, num: strikeNum } = this.parseAddOnPriceField(
      row.single_strikeoff_price
    );
    // Duplicate → POST new package: never send source variant ids (backend creates new rows).
    const isDuplicateFlow = !!(this.packageData as { isDuplicate?: boolean } | null)?.isDuplicate;
    if (!isDuplicateFlow && row.id != null && String(row.id).trim() !== '') {
      v.id = String(row.id).trim();
    }
    if (listStr !== '' && !isNaN(listNum)) {
      v.single_list_price = listNum;
    } else if (allowsCustom) {
      v.single_list_price = null;
    }
    if (strikeStr !== '' && !isNaN(strikeNum) && strikeNum > 0) {
      v.single_strikeoff_price = strikeNum;
    } else if (allowsCustom) {
      v.single_strikeoff_price = null;
    }
    return v;
  }

  createAddOnVariantGroup(
    v?: Record<string, any> | null,
    opts?: { placeholderVariantName?: string; variantNameOptional?: boolean }
  ): FormGroup {
    const initialName =
      v && typeof (v as { variant_name?: string }).variant_name === 'string'
        ? (v as { variant_name: string }).variant_name
        : (opts?.placeholderVariantName ?? '');
    const g = this.fb.group(
      {
        id: [
          v != null && (v as { id?: any }).id != null && String((v as { id: any }).id).trim() !== ''
            ? String((v as { id: string | number }).id)
            : '',
        ],
        variant_name: opts?.variantNameOptional
          ? [initialName]
          : [initialName, Validators.required],
        single_list_price: [
          v &&
          (v as { single_list_price?: any }).single_list_price != null &&
          (v as { single_list_price: any }).single_list_price !== ''
            ? String((v as { single_list_price: any }).single_list_price)
            : '',
          [
            this.maxListPriceValidator(),
            this.listPriceLessThanStrikeoffPriceValidator('single_strikeoff_price'),
          ],
        ],
        single_strikeoff_price: [
          this.normalizeStrikeoffPriceForForm(
            (v as { single_strikeoff_price?: any } | null)?.single_strikeoff_price
          ),
          [
            this.maxListPriceValidator(),
            this.strikeoffPriceGreaterThanListPriceValidator('single_list_price'),
          ],
        ],
        allows_custom_price: [
          this.isAllowsCustomPriceValue(
            (v as { allows_custom_price?: any } | null)?.allows_custom_price
          ),
        ],
        quantity_selection: [
          ((v as { quantity_selection?: string } | null)?.quantity_selection as string) || 'single',
        ],
        is_active: [(v as { is_active?: boolean } | null)?.is_active !== false],
      },
      { validators: this.addOnVariantRowValidator }
    );
    this.syncAddOnVariantListStrikeValidators(g);
    g.get('allows_custom_price')?.valueChanges.subscribe(() => {
      this.syncAddOnVariantListStrikeValidators(g);
      // Must propagate to FormArray + root form or `packageForm.valid` stays stale (Save stays disabled).
      queueMicrotask(() => g.updateValueAndValidity({ emitEvent: true }));
    });
    return g;
  }

  /** Syncs detailing list/strike validators based on allows_custom_price. */
  private applyDetailingPricingValidators(): void {
    if (this.packageForm.get('package_type')?.value !== 'detailing') {
      return;
    }

    const singleList = this.packageForm.get('single_list_price');
    const singleStrike = this.packageForm.get('single_strikeoff_price');
    const custom = this.isAllowsCustomPriceValue(
      this.packageForm.get('allows_custom_price')?.value
    );

    if (custom) {
      singleList?.clearValidators();
      singleStrike?.clearValidators();
    } else {
      singleList?.setValidators([
        Validators.required,
        Validators.min(1),
        this.maxListPriceValidator(),
        this.listPriceLessThanStrikeoffPriceValidator('single_strikeoff_price'),
      ]);
      singleStrike?.setValidators([
        this.maxListPriceValidator(),
        this.strikeoffPriceGreaterThanListPriceValidator('single_list_price'),
      ]);
    }
    singleList?.updateValueAndValidity({ emitEvent: false });
    singleStrike?.updateValueAndValidity({ emitEvent: false });
    this.packageForm.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Keep only car-wash Type of Wash tokens. Detailing/add-on packages store `wash_types: ['detailing']`
   * / `['add_on']` on the API — those must not satisfy car-wash wash_types validation after a type switch.
   */
  private filterCarWashWashTypes(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    const allowed: readonly string[] = AddPackageModalComponent.CAR_WASH_WASH_TYPES;
    return value.filter(
      (item): item is string => typeof item === 'string' && allowed.includes(item)
    );
  }

  /**
   * When switching to Car Wash (especially from Detailing edit), drop non-car-wash wash_types tokens
   * and clear single-wash prices that belonged to the previous type so Save re-validates from scratch.
   */
  private normalizeCarWashWashTypesAfterPackageTypeChange(): void {
    const washTypes = this.packageForm.get('wash_types');
    const raw = washTypes?.value;
    const carWashOnly = this.filterCarWashWashTypes(raw);
    const changed =
      !Array.isArray(raw) ||
      raw.length !== carWashOnly.length ||
      carWashOnly.some((t, i) => t !== raw[i]);

    if (changed) {
      washTypes?.setValue(carWashOnly, { emitEvent: false });
    }

    if (!carWashOnly.includes('single_wash')) {
      this.packageForm.get('allows_custom_price')?.setValue(false, { emitEvent: false });
      this.packageForm.get('single_list_price')?.setValue('', { emitEvent: false });
      this.packageForm.get('single_strikeoff_price')?.setValue('', { emitEvent: false });
    }
  }

  /** Syncs single wash list/strike validators for car wash based on wash_types and allows_custom_price. */
  private applyCarWashSingleWashPricingValidators(): void {
    const packageType = this.packageForm.get('package_type')?.value;
    const wash = (this.packageForm.get('wash_types')?.value as string[]) || [];
    const single = wash.includes('single_wash');
    const singleList = this.packageForm.get('single_list_price');
    const singleStrike = this.packageForm.get('single_strikeoff_price');

    if (packageType !== 'car_wash') {
      return;
    }

    if (!single) {
      singleList?.clearValidators();
      singleStrike?.clearValidators();
    } else {
      const custom = this.isAllowsCustomPriceValue(
        this.packageForm.get('allows_custom_price')?.value
      );
      if (custom) {
        singleList?.clearValidators();
        singleStrike?.clearValidators();
      } else {
        singleList?.setValidators([
          Validators.required,
          Validators.min(1),
          this.maxListPriceValidator(),
          this.listPriceLessThanStrikeoffPriceValidator('single_strikeoff_price'),
        ]);
        singleStrike?.setValidators([
          this.maxListPriceValidator(),
          this.strikeoffPriceGreaterThanListPriceValidator('single_list_price'),
        ]);
      }
    }
    singleList?.updateValueAndValidity({ emitEvent: false });
    singleStrike?.updateValueAndValidity({ emitEvent: false });
    this.packageForm.updateValueAndValidity({ emitEvent: false });
  }

  /** Per-month memberships need a positive wash count; empty/0 is stored as unlimited. */
  private syncWashCountFrequencyValidators(): void {
    const frequency = this.packageForm.get('wash_count_frequency');
    if (!frequency) {
      return;
    }
    const requireFrequency =
      this.isMonthlyMembershipSelected() &&
      this.packageForm.get('wash_count_period')?.value === 'per_month';
    frequency.setValidators(
      requireFrequency
        ? [Validators.required, Validators.min(1), this.maxDigitsValidator(3)]
        : [this.maxDigitsValidator(3)]
    );
    frequency.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Monthly price fields only apply when "Monthly Membership" is selected. The constructor used to attach
   * `Validators.min(1)` to `monthly_list_price` always; after edit/duplicate `patchValue(..., { emitEvent: false })`
   * `wash_types` does not emit, so those validators were never relaxed — `packageForm` stayed invalid and Save disabled.
   */
  private syncCarWashMonthlyPricingValidators(): void {
    this.syncWashCountFrequencyValidators();
    if (this.packageForm.get('package_type')?.value !== 'car_wash') {
      return;
    }
    const wash = (this.packageForm.get('wash_types')?.value as string[]) || [];
    const monthlyList = this.packageForm.get('monthly_list_price');
    const monthlyStrike = this.packageForm.get('monthly_strikeoff_price');

    if (wash.includes('monthly_membership')) {
      monthlyList?.setValidators([
        Validators.required,
        Validators.min(1),
        this.maxListPriceValidator(),
        this.listPriceLessThanStrikeoffPriceValidator('monthly_strikeoff_price'),
      ]);
      monthlyStrike?.setValidators([
        this.maxListPriceValidator(),
        this.strikeoffPriceGreaterThanListPriceValidator('monthly_list_price'),
      ]);
    } else {
      monthlyList?.clearValidators();
      monthlyStrike?.clearValidators();
    }
    monthlyList?.updateValueAndValidity({ emitEvent: false });
    monthlyStrike?.updateValueAndValidity({ emitEvent: false });
    this.packageForm.updateValueAndValidity({ emitEvent: false });
  }

  constructor(
    private fb: FormBuilder,
    private packageService: PackageService,
    private profileService: ProfileService,
    private messageService: NotificationService,
    private router: Router,
    private appConfigService: AppConfigService
  ) {
    this.packageForm = this.fb.group({
      package_name: ['', [Validators.required, mustContainAlphanumericValidator()]],
      package_status: [true],
      package_type: ['car_wash', Validators.required],
      wash_types: [['single_wash'], Validators.required],
      paired_with: [[] as string[]],
      is_defender: [false],
      variants: this.fb.array([]),
      single_list_price: [
        '',
        [
          Validators.min(1),
          this.maxListPriceValidator(),
          this.listPriceLessThanStrikeoffPriceValidator('single_strikeoff_price'),
        ],
      ],
      single_strikeoff_price: [
        '',
        [
          this.maxListPriceValidator(),
          this.strikeoffPriceGreaterThanListPriceValidator('single_list_price'),
        ],
      ],
      // Validators applied in `syncCarWashMonthlyPricingValidators` when Monthly Membership is selected.
      monthly_list_price: [''],
      monthly_strikeoff_price: [''],
      wash_count_period: ['unlimited'],
      // Required + min(1) applied in `syncWashCountFrequencyValidators` when Per month is selected.
      wash_count_frequency: [null, this.maxDigitsValidator(3)],
      family_eligible: ['no'],
      locations: [[], Validators.required],
      features: [[], Validators.required],
      license_plate_needed: ['no', Validators.required],
      theme_color: [DEFAULT_THEME_BACKGROUND],
      allows_custom_price: [false],
    });
  }

  get variants(): FormArray {
    return this.packageForm.get('variants') as FormArray;
  }

  /**
   * Add-on packages do not use root wash_types / list prices / features the same way as car wash.
   * `patchValue(..., { emitEvent: false })` skips `package_type` valueChanges, so we must sync validators
   * here or Save stays disabled on Edit (e.g. wash_types `required` + `[]` is invalid).
   */
  private syncAddOnRootAndWashValidators(): void {
    const washTypes = this.packageForm.get('wash_types');
    const familyEligible = this.packageForm.get('family_eligible');
    const singleListPrice = this.packageForm.get('single_list_price');
    const monthlyListPrice = this.packageForm.get('monthly_list_price');
    const singleStrike = this.packageForm.get('single_strikeoff_price');
    const monthlyStrike = this.packageForm.get('monthly_strikeoff_price');

    washTypes?.clearValidators();
    washTypes?.setValue([], { emitEvent: false });
    washTypes?.updateValueAndValidity({ emitEvent: false });

    familyEligible?.setValue('no', { emitEvent: false });

    singleListPrice?.clearValidators();
    monthlyListPrice?.clearValidators();
    singleListPrice?.setValue('', { emitEvent: false });
    monthlyListPrice?.setValue('', { emitEvent: false });
    singleListPrice?.updateValueAndValidity({ emitEvent: false });
    monthlyListPrice?.updateValueAndValidity({ emitEvent: false });

    singleStrike?.clearValidators();
    monthlyStrike?.clearValidators();
    singleStrike?.updateValueAndValidity({ emitEvent: false });
    monthlyStrike?.updateValueAndValidity({ emitEvent: false });

    this.packageForm.get('allows_custom_price')?.setValue(false, { emitEvent: false });

    this.packageForm.get('features')?.clearValidators();
    this.packageForm.get('features')?.updateValueAndValidity({ emitEvent: false });
  }

  ngOnInit(): void {
    this.loadFamilyPlanFeature();
    // Initialize features first (before subscription that uses them)
    this.features = [
      { name: 'Exterior wash', value: 'exterior_wash' },
      { name: 'Wheel cleaning', value: 'wheel_cleaning' },
      { name: 'Spot Free rinse', value: 'spot_free_rinse' },
      { name: 'Rain repellant', value: 'rain_repellant' },
      {
        name: 'Vacuuming Floor, seats, floor mats',
        value: 'vacuuming_floor_seats_mats',
      },
      { name: 'Interior Window cleaning', value: 'interior_window_cleaning' },
      { name: 'Light dusting', value: 'light_dusting' },
      {
        name: 'Carpet & Upholstery Shampooing',
        value: 'carpet_upholstery_shampooing',
      },
      { name: 'Steam Cleaning', value: 'steam_cleaning' },
      {
        name: 'Leather Cleaning & Conditioning',
        value: 'leather_cleaning_conditioning',
      },
      { name: 'Headliner Cleaning', value: 'headliner_cleaning' },
      { name: 'Odor Elimination', value: 'odor_elimination' },
      { name: 'Interior Protectant', value: 'interior_protectant' },
      { name: 'Paint Correction', value: 'paint_correction' },
      { name: 'Clay Bar Treatment', value: 'clay_bar_treatment' },
      {
        name: 'Professional Ceramic Coating',
        value: 'professional_ceramic_coating',
      },
      { name: 'Headlight Restoration', value: 'headlight_restoration' },
      { name: 'Engine Bay Cleaning', value: 'engine_bay_cleaning' },
      { name: 'Trim Restoration', value: 'trim_restoration' },
      { name: 'Touchless Wash', value: 'touchless_wash' },
      { name: 'Soft-Touch / Friction Wash', value: 'soft_touch_friction_wash' },
      { name: 'Full-Service / Hand Wash', value: 'full_service_hand_wash' },
      { name: 'Self-Service Bays', value: 'self_service_bays' },
      { name: 'In-Bay Automatic (IBA)', value: 'in_bay_automatic_iba' },
      { name: 'Bug Prep/Bug Buster', value: 'bug_prep_bug_buster' },
      { name: 'Foam Cannon / Foam Wall', value: 'foam_cannon_foam_wall' },
      { name: 'Underbody/Chassis Wash', value: 'underbody_chassis_wash' },
      { name: 'Clear Coat Protectant', value: 'clear_coat_protectant' },
      { name: 'Spray Wax / Hot Wax', value: 'spray_wax_hot_wax' },
      { name: 'Tri-Color Foam Polish', value: 'tri_color_foam_polish' },
      {
        name: 'Ceramic Sealants / Ceramic Wax',
        value: 'ceramic_sealants_ceramic_wax',
      },
      { name: 'Graphene Coatings', value: 'graphene_coatings' },
      {
        name: 'Rain Repellent Glass Treatment',
        value: 'rain_repellent_glass_treatment',
      },
      { name: 'Hand/Towel Dry', value: 'hand_towel_dry' },
      { name: 'Door jamb cleaning', value: 'door_jamb_cleaning' },
      { name: 'Fragrance spray', value: 'fragrance_spray' },
      { name: 'Pet hair removal', value: 'pet_hair_removal' },
      { name: 'Leather conditioning', value: 'leather_conditioning' },
      { name: 'Pre-Wash Shampoo', value: 'pre_wash_shampoo' },
      { name: 'Cloth Power Wash', value: 'cloth_power_wash' },
      { name: 'Turbo Dry', value: 'turbo_dry' },
      { name: 'Free Vacuum', value: 'free_vacuum' },
      {
        name: 'High-Pressure Tire Cleaning',
        value: 'high_pressure_tire_cleaning',
      },
      {
        name: 'Triple Color Wax & Polish',
        value: 'triple_color_wax_and_polish',
      },
      { name: 'Double Tire/Wheel Shampoo', value: 'double_tire_wheel_shampoo' },
      {
        name: 'Rain-X Clear Coat Body Protection',
        value: 'rain_x_clear_coat_body_protection',
      },

      { name: 'High Pressure Rinse', value: 'high_pressure_rinse' },
      { name: 'Wash & Dry', value: 'wash_and_dry' },
      { name: 'Lava Foam', value: 'lava_foam' },
      { name: 'Sealer Wax', value: 'sealer_wax' },
      { name: 'Wheel Cleaner', value: 'wheel_cleaner' },
      { name: 'Triple Foam', value: 'triple_foam' },
      { name: 'Ceramic Wax', value: 'ceramic_wax' },
      { name: 'Clear Coat Protection', value: 'clear_coat_protection' },
      { name: 'Under Carriage Wash', value: 'under_carriage_wash' },
      { name: 'Tire Shine', value: 'tire_shine' },
      { name: 'Superior Shine Finish', value: 'superior_shine_finish' },
      { name: 'Extra Protection Boost', value: 'extra_protection_boost' },
      { name: 'Tunnel Wash', value: 'tunnel_wash' },
      { name: 'Air Dry', value: 'air_dry' },
      { name: 'Undercarriage Blast', value: 'undercarriage_blast' },
      { name: 'Soap + Water', value: 'soap_water' },
      { name: 'Free Compressed Air', value: 'free_compressed_air' },
      { name: 'Ceramic 2X Coating', value: 'ceramic_2x_coating' },
      { name: 'Ceramic 5X Coating', value: 'ceramic_5x_coating' },
      { name: 'Wheel & Tire Scrubber', value: 'wheel_and_tire_scrubber' },
      { name: 'Buff-N-Shine', value: 'buff_n_shine' },

      // Car Wash
      {
        name: 'Hand Wash And Towel Dry Exterior',
        value: 'hand_wash_and_towel_dry_exterior',
      },
      { name: 'Clean Windows', value: 'clean_windows' },
      {
        name: 'Light Wipe-Down Of Dashboard',
        value: 'light_wipe_down_of_dashboard',
      },
      { name: 'Interior Vacuum', value: 'interior_vacuum' },
      { name: 'Tornado Air Cleaning', value: 'tornado_air_cleaning' },
      { name: 'Rubber Floor Mat Wash', value: 'rubber_floor_mat_wash' },

      // Detailing
      { name: 'Orbital Polish', value: 'orbital_polish' },
      { name: 'Sealant Wax', value: 'sealant_wax' },
      {
        name: 'Clean & Dress Exterior: Rubber, Vinyl & Plastic',
        value: 'clean_and_dress_exterior_rubber_vinyl_plastic',
      },
      { name: 'Full Wheel Detail', value: 'full_wheel_detail' },
      {
        name: 'Clean & Dress Leather Seats',
        value: 'clean_and_dress_leather_seats',
      },
      {
        name: 'Shampoo Carpets & Cloth Seats',
        value: 'shampoo_carpets_and_cloth_seats',
      },
      {
        name: 'Clean & Condition: Console, Dash & Doors',
        value: 'clean_and_condition_console_dash_doors',
      },
      { name: 'Shampoo Trunk', value: 'shampoo_trunk' },
      { name: 'Spot Clean Headliner', value: 'spot_clean_headliner' },
      { name: 'Clean Door Jams', value: 'clean_door_jams' },
    ];

    if (this.packageData) {
      this.tunnelSelections = tunnelSelectionsFromPackage(this.packageData.locations);
    }

    this.profileService.locations$.subscribe(locations => {
      this.locations = locations;
      if (this.packageData) {
        // Prepare the patch data
        const patchData: any = {
          ...this.packageData,
          package_status: this.packageData.package_status,
          family_eligible: this.packageData.family_eligible ? 'yes' : 'no',
          license_plate_needed: this.packageData.license_plate_needed ? 'yes' : 'no',
          locations:
            this.packageData.locations
              ?.map((l: any) => this.locations.find(loc => loc.id === l.id))
              .filter((l: any) => l) || [],
          features:
            this.packageData.features
              ?.map((f: any) => {
                // Handle both string names and feature objects
                const featureName = typeof f === 'string' ? f : f.name || f;
                return this.features.find(feat => feat.name === featureName);
              })
              .filter((f: any) => f !== undefined && f !== null) || [],
          package_type: this.packageData.type,
        };

        if (this.packageData.type === 'add_on') {
          patchData.wash_types = [];
          patchData.paired_with =
            this.packageData.paired_with && Array.isArray(this.packageData.paired_with)
              ? [...this.packageData.paired_with]
              : ['single_wash'];
          delete patchData.variants;
        }

        // Map old detailing field names to new single field names for backwards compatibility
        const pkgType = this.packageData.type || this.packageData.package_type;
        if (pkgType === 'detailing') {
          if (this.packageData.detailing_list_price !== undefined) {
            patchData.single_list_price = this.packageData.detailing_list_price;
          }
          if (this.packageData.detailing_strikeoff_price !== undefined) {
            patchData.single_strikeoff_price = this.packageData.detailing_strikeoff_price;
          }
        }

        patchData.single_strikeoff_price = this.normalizeStrikeoffPriceForForm(
          patchData.single_strikeoff_price
        );
        patchData.monthly_strikeoff_price = this.normalizeStrikeoffPriceForForm(
          patchData.monthly_strikeoff_price
        );

        const washCount = this.resolveWashCountFormValues(this.packageData);
        patchData.wash_count_period = washCount.period;
        patchData.wash_count_frequency = washCount.frequency;

        // Explicit theme so missing API values don't leave FormBuilder DEFAULT (which would overwrite on save).
        // Unset theme stays null and is omitted on update; duplicates get the create default.
        const rawTheme = this.packageData.theme_color;
        patchData.theme_color =
          rawTheme != null && String(rawTheme).trim() !== ''
            ? rawTheme
            : this.packageData.isDuplicate
              ? DEFAULT_THEME_BACKGROUND
              : null;

        try {
          this.packageForm.patchValue(patchData, { emitEvent: false });
          this.refreshTunnelWashFields();
          if (this.packageData.type === 'add_on') {
            this.variants.clear();
            const rows =
              this.packageData.variants && Array.isArray(this.packageData.variants)
                ? this.packageData.variants
                : [];
            for (const row of rows) {
              this.variants.push(this.createAddOnVariantGroup(row as Record<string, any>));
            }
            this.variantSavedStates = rows.length ? Array(rows.length).fill(true) : [];
            this.variantKebabOpen = rows.length ? Array(rows.length).fill(false) : [];
            if (rows.length === 0) {
              this.ensureDefaultAddOnVariantDraft(
                this.buildDefaultAddOnVariantFromRoot(this.packageData)
              );
            }
            this.variants.setValidators(this.addOnVariantsArrayMinLength(1));
            this.variants.updateValueAndValidity({ emitEvent: false });
            this.variants.enable({ emitEvent: false });
            this.applyAddOnPairedValidators();
            this.syncAddOnRootAndWashValidators();
          }
          // Trigger validation after patching
          setTimeout(() => {
            this.applyCarWashSingleWashPricingValidators();
            this.applyDetailingPricingValidators();
            this.syncCarWashMonthlyPricingValidators();

            this.packageForm.updateValueAndValidity({ emitEvent: false });
            if (this.packageData?.type === 'add_on') {
              this.syncAddOnRootAndWashValidators();
              this.variants.setValidators(this.addOnVariantsArrayMinLength(1));
              this.variants.updateValueAndValidity({ emitEvent: false });
              this.applyAddOnPairedValidators();
            }
          }, 0);
        } catch (error) {
          console.warn('Error patching form data:', error);
        }
      }
    });

    // Initialize locations load
    this.profileService.loadLocations();

    // Package type validation
    this.packageForm.get('package_type')?.valueChanges.subscribe(value => {
      const washTypes = this.packageForm.get('wash_types');
      const singleListPrice = this.packageForm.get('single_list_price');
      const monthlyListPrice = this.packageForm.get('monthly_list_price');

      if (value === 'car_wash') {
        washTypes?.setValidators(Validators.required);
        // Drop detailing/add_on wash_types tokens and stale detailing prices before re-validating.
        this.normalizeCarWashWashTypesAfterPackageTypeChange();
        // Clear single list price validation - it will be set by wash type selection
        singleListPrice?.clearValidators();
        this.variants.clearValidators();
        this.variants.updateValueAndValidity({ emitEvent: false });
        this.variants.disable({ emitEvent: false });
        this.applyAddOnPairedValidators();
        this.packageForm.get('features')?.setValidators(Validators.required);
        this.packageForm.get('features')?.updateValueAndValidity({ emitEvent: false });
        this.applyCarWashSingleWashPricingValidators();
        this.syncCarWashMonthlyPricingValidators();
      } else if (value === 'detailing') {
        this.packageForm.get('allows_custom_price')?.setValue(false, { emitEvent: false });
        washTypes?.clearValidators();
        washTypes?.setValue([]);
        this.packageForm.get('family_eligible')?.setValue('no');
        monthlyListPrice?.clearValidators();
        monthlyListPrice?.setValue('');
        this.variants.clearValidators();
        this.variants.updateValueAndValidity({ emitEvent: false });
        this.variants.disable({ emitEvent: false });
        this.applyAddOnPairedValidators();
        this.packageForm.get('features')?.setValidators(Validators.required);
        this.packageForm.get('features')?.updateValueAndValidity({ emitEvent: false });
        this.applyDetailingPricingValidators();
      } else if (value === 'add_on') {
        this.syncAddOnRootAndWashValidators();
        const editingExistingAddOn = !!(
          this.packageData && (this.packageData as { type?: string }).type === 'add_on'
        );
        if (!editingExistingAddOn) {
          this.variants.clear();
          this.variantSavedStates = [];
          this.variantKebabOpen = [];
          this.ensureDefaultAddOnVariantDraft(
            this.buildDefaultAddOnVariantFromRoot(this.packageData)
          );
        }
        this.variants.setValidators(this.addOnVariantsArrayMinLength(1));
        this.variants.updateValueAndValidity({ emitEvent: false });
        this.variants.enable({ emitEvent: false });
        this.applyAddOnPairedValidators();
        const paired = this.packageForm.get('paired_with')?.value;
        if (!Array.isArray(paired) || paired.length === 0) {
          this.packageForm.get('paired_with')?.setValue(['single_wash'], { emitEvent: false });
        }
      }

      washTypes?.updateValueAndValidity();
      singleListPrice?.updateValueAndValidity();
      monthlyListPrice?.updateValueAndValidity();
      this.syncWashCountFrequencyValidators();
    });

    this.packageForm.get('wash_types')?.valueChanges.subscribe(value => {
      // Family eligibility needs a monthly membership; reset it when that wash type is dropped.
      if (!Array.isArray(value) || !value.includes('monthly_membership')) {
        this.packageForm.get('family_eligible')?.setValue('no', { emitEvent: false });
      }
      // Only apply wash type validations if this is a car wash package
      const packageType = this.packageForm.get('package_type')?.value;
      if (packageType === 'car_wash') {
        // Strip detailing/add_on tokens even when they are the only values (edit after detailing).
        if (Array.isArray(value)) {
          const filteredValue = this.filterCarWashWashTypes(value);
          if (
            filteredValue.length !== value.length ||
            filteredValue.some((t, i) => t !== value[i])
          ) {
            this.packageForm.get('wash_types')?.setValue(filteredValue, { emitEvent: false });
            value = filteredValue;
          }
        }

        if (Array.isArray(value) && !value.includes('single_wash')) {
          this.packageForm.get('allows_custom_price')?.setValue(false, { emitEvent: false });
        }
        this.applyCarWashSingleWashPricingValidators();
        this.syncCarWashMonthlyPricingValidators();
      }
    });

    this.packageForm.get('wash_count_period')?.valueChanges.subscribe(value => {
      if (value === 'unlimited') {
        this.packageForm.get('wash_count_frequency')?.setValue(null);
      }
      this.syncWashCountFrequencyValidators();
    });

    this.packageForm.get('allows_custom_price')?.valueChanges.subscribe(() => {
      this.applyCarWashSingleWashPricingValidators();
      this.applyDetailingPricingValidators();
    });

    this.packageForm.get('single_list_price')?.valueChanges.subscribe(() => {
      try {
        // Revalidate strike-off price when list price changes
        this.packageForm
          .get('single_strikeoff_price')
          ?.updateValueAndValidity({ emitEvent: false });
        this.packageForm.updateValueAndValidity({ emitEvent: false });
      } catch (error) {
        console.warn('Error updating validity for single_list_price:', error);
      }
    });

    this.packageForm.get('monthly_list_price')?.valueChanges.subscribe(() => {
      try {
        // Revalidate strike-off price when list price changes
        this.packageForm
          .get('monthly_strikeoff_price')
          ?.updateValueAndValidity({ emitEvent: false });
      } catch (error) {
        console.warn('Error updating validity for monthly_list_price:', error);
      }
    });

    // Revalidate list price when strike-off price changes
    this.packageForm.get('single_strikeoff_price')?.valueChanges.subscribe(() => {
      try {
        this.packageForm.get('single_list_price')?.updateValueAndValidity({ emitEvent: false });
        this.packageForm.updateValueAndValidity({ emitEvent: false });
      } catch (error) {
        console.warn('Error updating validity for single_strikeoff_price:', error);
      }
    });

    this.packageForm.get('monthly_strikeoff_price')?.valueChanges.subscribe(() => {
      try {
        this.packageForm.get('monthly_list_price')?.updateValueAndValidity({ emitEvent: false });
      } catch (error) {
        console.warn('Error updating validity for monthly_strikeoff_price:', error);
      }
    });

    // Trigger initial validation for package type and wash types
    try {
      this.packageForm.get('package_type')?.updateValueAndValidity({ emitEvent: false });
      this.packageForm.get('wash_types')?.updateValueAndValidity({ emitEvent: false });
      this.applyCarWashSingleWashPricingValidators();
      this.applyDetailingPricingValidators();
      this.syncCarWashMonthlyPricingValidators();
    } catch (error) {
      console.warn('Error during initial validation:', error);
    }

    // Track form changes
    this.packageForm.valueChanges.subscribe(() => {
      this.hasFormChanges = this.packageForm.dirty;
      this.refreshTunnelWashFields();
    });
    this.refreshTunnelWashFields();

    if (!this.packageData || (this.packageData as { type?: string }).type !== 'add_on') {
      this.variants.disable({ emitEvent: false });
    }
    this.applyAddOnPairedValidators();
  }

  ngOnDestroy(): void {
    this.tunnelWashOptionsSub?.unsubscribe();
  }

  /** Selected locations that are car wash eligible; [] hides every tunnel field. */
  private tunnelWashEligibleLocations(): { id: string; branch?: string; name?: string }[] {
    const value = this.packageForm.getRawValue() as {
      package_type?: string;
      wash_types?: string[];
      locations?: { id: string; branch?: string; name?: string }[];
    };
    if (!isTunnelWashEligiblePackage(value.package_type, value.wash_types)) {
      return [];
    }
    return Array.isArray(value.locations) ? value.locations.filter(l => l && l.id) : [];
  }

  /**
   * Rebuilds the tunnel fields from the form, fetching the options again whenever the set
   * of selected locations changes. A failed fetch shows no field (nothing is sent).
   */
  refreshTunnelWashFields(): void {
    const locations = this.tunnelWashEligibleLocations();
    const ids = tunnelWashLocationIds(locations);
    const key = ids.join(',');
    if (key !== this.tunnelWashOptionsKey) {
      this.tunnelWashOptionsKey = key;
      this.tunnelWashOptionsSub?.unsubscribe();
      this.tunnelWashOptions = [];
      if (ids.length) {
        this.tunnelWashOptionsSub = this.packageService.getTunnelWashPackages(ids).subscribe({
          next: options => {
            if (key !== this.tunnelWashOptionsKey) {
              return;
            }
            this.tunnelWashOptions = options;
            this.tunnelWashFields = buildTunnelWashFields(
              this.tunnelWashEligibleLocations(),
              options
            );
          },
          error: () => {
            this.tunnelWashOptions = [];
            this.tunnelWashFields = [];
          },
        });
      }
    }
    this.tunnelWashFields = buildTunnelWashFields(locations, this.tunnelWashOptions);
  }

  trackTunnelWashField(_: number, field: TunnelWashField): string {
    return field.locationId;
  }

  /** Native select: '' is the Manual option. */
  onTunnelWashSelect(locationId: string, event: Event): void {
    const value = (event.target as HTMLSelectElement | null)?.value ?? '';
    this.onTunnelWashChange(locationId, value || null);
  }

  tunnelWashValue(field: TunnelWashField): string | null {
    return resolveTunnelWashSelection(field, this.tunnelSelections);
  }

  onTunnelWashChange(locationId: string, value: string | null): void {
    this.tunnelSelections = { ...this.tunnelSelections, [locationId]: value || null };
    this.packageForm.markAsDirty();
    this.hasFormChanges = true;
  }

  isCarWashPackage(): boolean {
    return this.packageForm.get('package_type')?.value === 'car_wash';
  }

  isDetailingPackage(): boolean {
    return this.packageForm.get('package_type')?.value === 'detailing';
  }

  isAddOnPackage(): boolean {
    return this.packageForm.get('package_type')?.value === 'add_on';
  }

  isAddOnVariantCustomPrice(i: number): boolean {
    return this.isAllowsCustomPriceValue(this.variants.at(i)?.get('allows_custom_price')?.value);
  }

  /**
   * True when this variant row has any user interaction (dirty/touched child).
   * Used instead of `FormGroup.touched` so PrimeNG radio registry bugs / cross-row
   * updates do not show another row's validation messages.
   */
  showAddOnVariantInlineErrors(i: number): boolean {
    const row = this.variants.at(i) as FormGroup;
    if (!row) {
      return false;
    }
    const keys = [
      'variant_name',
      'single_list_price',
      'single_strikeoff_price',
      'allows_custom_price',
      'quantity_selection',
    ] as const;
    return keys.some(key => {
      const c = row.get(key);
      return !!(c && (c.dirty || c.touched));
    });
  }

  toggleCustomPrice(i: number): void {
    const ctrl = this.variants.at(i)?.get('allows_custom_price');
    if (ctrl) {
      ctrl.setValue(!this.isAllowsCustomPriceValue(ctrl.value), { emitEvent: true });
      ctrl.markAsDirty();
      const row = this.variants.at(i) as FormGroup;
      row.get('single_list_price')?.updateValueAndValidity({ emitEvent: false });
      row.get('single_strikeoff_price')?.updateValueAndValidity({ emitEvent: false });
      row.updateValueAndValidity({ emitEvent: true });
      this.variants.updateValueAndValidity({ emitEvent: true });
      this.packageForm.updateValueAndValidity({ emitEvent: false });
    }
  }

  onAddOnVariantCardSave(i: number): void {
    const row = this.variants.at(i) as FormGroup;
    row.markAllAsTouched();
    row.updateValueAndValidity();
    if (row.invalid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Incomplete variant',
        detail: this.getAddOnVariantInvalidSaveMessage(row, i),
      });
      return;
    }
    this.variantSavedStates[i] = true;
    this.variantKebabOpen[i] = false;
  }

  /** User-facing copy aligned with inline errors on the variant card. */
  private getAddOnVariantInvalidSaveMessage(row: FormGroup, i: number): string {
    const nameCtrl = row.get('variant_name');
    if (nameCtrl?.hasError('required')) {
      return 'Name is required';
    }
    if (!this.isAddOnVariantCustomPrice(i) && row.hasError('listRequired')) {
      return 'Enter a list price, or select Custom price.';
    }
    if (row.get('single_strikeoff_price')?.hasError('strikeoffPriceNotGreaterThanListPrice')) {
      return 'Strike-off price must be greater than list price.';
    }
    if (row.get('single_strikeoff_price')?.hasError('strikeoffPriceCannotBeZero')) {
      return 'Strike-off price cannot be 0.';
    }
    if (row.get('single_list_price')?.hasError('listPriceMustBeLessThanStrikeoffPrice')) {
      return 'List price must be less than strike-off price.';
    }
    return 'Please fix the highlighted fields before saving.';
  }

  renameVariant(i: number): void {
    this.variantSavedStates[i] = false;
    this.variantKebabOpen[i] = false;
  }

  toggleVariantKebab(i: number, event: Event): void {
    event.stopPropagation();
    const wasOpen = this.variantKebabOpen[i];
    this.variantKebabOpen = this.variantKebabOpen.map(() => false);
    this.variantKebabOpen[i] = !wasOpen;
  }

  closeAllKebabs(): void {
    this.variantKebabOpen = this.variantKebabOpen.map(() => false);
  }

  hasAnySavedVariants(): boolean {
    return this.variantSavedStates.some(s => s);
  }

  onAddOnVariantListInput(event: Event, i: number): void {
    const c = this.variants.at(i)?.get('single_list_price');
    if (c) {
      this.mirrorListPriceEventToControl(event, c);
      this.variants
        .at(i)
        ?.get('single_strikeoff_price')
        ?.updateValueAndValidity({ emitEvent: false });
    }
  }

  onAddOnVariantStrikeoffInput(event: Event, i: number): void {
    const c = this.variants.at(i)?.get('single_strikeoff_price');
    if (c) {
      this.mirrorStrikeoffEventToControl(event, c);
      this.variants.at(i)?.get('single_list_price')?.updateValueAndValidity({ emitEvent: false });
    }
  }

  private mirrorListPriceEventToControl(event: Event, control: AbstractControl): void {
    this.applySanitizedPriceToAbstractControl(event, control, { blockZero: false });
  }

  private mirrorStrikeoffEventToControl(event: Event, control: AbstractControl): void {
    this.applySanitizedPriceToAbstractControl(event, control, { blockZero: true });
  }

  /** Shared path for add-on variant list/strike inputs (always setValue). */
  private applySanitizedPriceToAbstractControl(
    event: Event,
    control: AbstractControl,
    options: { blockZero: boolean }
  ): void {
    const target = event.target as HTMLInputElement;
    const value = sanitizePriceInputValue(target.value, options);
    if (value === null) {
      target.value = '';
      control.setValue('', { emitEvent: false });
      control.updateValueAndValidity({ emitEvent: false });
      return;
    }
    if (target.value !== value) {
      target.value = value;
    }
    control.setValue(value, { emitEvent: false });
    control.updateValueAndValidity({ emitEvent: false });
  }

  private minArrayLength(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const v = control.value;
      if (Array.isArray(v) && v.length >= min) {
        return null;
      }
      return { minPaired: { min, actual: Array.isArray(v) ? v.length : 0 } };
    };
  }

  /** Require at least one variant when package type is Add on. */
  private addOnVariantsArrayMinLength(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.packageForm.get('package_type')?.value !== 'add_on') {
        return null;
      }
      const fa = control as FormArray;
      const n = fa?.length ?? 0;
      return n >= min ? null : { minVariants: { min, actual: n } };
    };
  }

  addAddOnVariant(): void {
    const wasSingleImplicitRow = this.variants.length === 1 && this.isAddOnDefaultPricingOnlyRow(0);
    this.variants.push(this.createAddOnVariantGroup());
    if (wasSingleImplicitRow) {
      const row0 = this.variants.at(0) as FormGroup;
      row0.get('variant_name')?.setValidators([Validators.required]);
      row0.get('variant_name')?.updateValueAndValidity({ emitEvent: false });
    }
    this.variantSavedStates.push(false);
    this.variantKebabOpen.push(false);
  }

  /** One draft row so List / Strike / Custom price show as soon as Add on is selected; quantity is per-variant only (hidden until multiple variant rows). */
  private ensureDefaultAddOnVariantDraft(seed?: Record<string, any> | null): void {
    if (this.variants.length > 0) {
      return;
    }
    this.variants.push(this.createAddOnVariantGroup(seed ?? null, { variantNameOptional: true }));
    this.variantSavedStates = [false];
    this.variantKebabOpen = [false];
    this.variants.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * For add-ons saved without explicit variants, API stores prices on root package fields.
   * Seed the default draft row from those root prices so duplicate/edit keeps Save enabled.
   */
  private buildDefaultAddOnVariantFromRoot(pkg: any): Record<string, any> | null {
    if (!pkg || (pkg.type || '').toLowerCase() !== 'add_on') {
      return null;
    }
    const listRaw = pkg.single_list_price;
    const strikeRaw = pkg.single_strikeoff_price;
    const listStr = listRaw != null && String(listRaw).trim() !== '' ? String(listRaw) : '';
    const strikeStr = this.normalizeStrikeoffPriceForForm(strikeRaw);
    const allowsCustom =
      this.isAllowsCustomPriceValue(pkg.allows_custom_price) ||
      this.isAllowsCustomPriceValue(pkg.custom_price);
    if (!listStr && !strikeStr && !allowsCustom) {
      return null;
    }
    return {
      single_list_price: listStr,
      single_strikeoff_price: strikeStr,
      quantity_selection: 'single',
      allows_custom_price: allowsCustom,
      is_active: true,
    };
  }

  /** First default row: pricing + quantity only (no name / inner actions). */
  isAddOnDefaultPricingOnlyRow(i: number): boolean {
    return this.variants.length === 1 && i === 0;
  }

  removeAddOnVariant(index: number): void {
    this.variants.removeAt(index);
    this.variantSavedStates.splice(index, 1);
    this.variantKebabOpen.splice(index, 1);
    if (this.isAddOnPackage() && this.variants.length === 0) {
      this.ensureDefaultAddOnVariantDraft();
    } else if (this.isAddOnPackage() && this.variants.length === 1) {
      const row0 = this.variants.at(0) as FormGroup;
      row0.get('variant_name')?.clearValidators();
      row0.get('variant_name')?.updateValueAndValidity({ emitEvent: false });
    }
  }

  private applyAddOnPairedValidators(): void {
    const c = this.packageForm.get('paired_with');
    if (this.isAddOnPackage()) {
      c?.setValidators(this.minArrayLength(1));
    } else {
      c?.clearValidators();
      c?.setValue([], { emitEvent: false });
    }
    c?.updateValueAndValidity({ emitEvent: false });
  }

  isSingleWashSelected(): boolean {
    return (
      this.isCarWashPackage() && this.packageForm.get('wash_types')?.value.includes('single_wash')
    );
  }

  /** Car wash single wash: allow staff-entered price at checkout (matches package API + notification modal). */
  isSingleWashCustomPrice(): boolean {
    return (
      this.isCarWashPackage() &&
      this.isAllowsCustomPriceValue(this.packageForm.get('allows_custom_price')?.value)
    );
  }

  toggleSingleWashCustomPrice(): void {
    const ctrl = this.packageForm.get('allows_custom_price');
    if (!ctrl) return;
    ctrl.setValue(!this.isAllowsCustomPriceValue(ctrl.value), { emitEvent: true });
  }

  /** Detailing packages: allow staff-entered price at checkout (same root allows_custom_price as single wash). */
  isDetailingCustomPrice(): boolean {
    return (
      this.isDetailingPackage() &&
      this.isAllowsCustomPriceValue(this.packageForm.get('allows_custom_price')?.value)
    );
  }

  toggleDetailingCustomPrice(): void {
    const ctrl = this.packageForm.get('allows_custom_price');
    if (!ctrl) {
      return;
    }
    ctrl.setValue(!this.isAllowsCustomPriceValue(ctrl.value), { emitEvent: true });
  }

  isMonthlyMembershipSelected(): boolean {
    return (
      this.isCarWashPackage() &&
      this.packageForm.get('wash_types')?.value.includes('monthly_membership')
    );
  }

  /** Fails closed: until the config loads, or if it cannot, the family plan section stays hidden. */
  private loadFamilyPlanFeature(): void {
    this.appConfigService.getAppConfig().subscribe({
      next: config => {
        this.familyPlanFeatureOn = isFeatureOn(config, FAMILY_PLAN_FEATURE);
      },
      error: () => {
        this.familyPlanFeatureOn = false;
      },
    });
  }

  /** Family eligibility toggle: flag on for the company and a monthly membership selected. */
  showFamilyEligible(): boolean {
    return this.familyPlanFeatureOn && this.isMonthlyMembershipSelected();
  }

  isWashCountPeriodUnlimited(): boolean {
    return this.packageForm.get('wash_count_period')?.value === 'unlimited';
  }

  /**
   * Bind stored wash-count fields on edit. Omitted API fields must not keep the create default.
   */
  resolveWashCountFormValues(packageData: Record<string, unknown> | null | undefined): {
    period: string;
    frequency: number | null;
  } {
    const unlimited = { period: 'unlimited', frequency: null as number | null };
    if (!packageData) {
      return unlimited;
    }
    const hasPeriod = Object.prototype.hasOwnProperty.call(packageData, 'wash_count_period');
    const hasFreq = Object.prototype.hasOwnProperty.call(packageData, 'wash_count_frequency');
    if (!hasPeriod && !hasFreq) {
      return unlimited;
    }
    const period = String(packageData['wash_count_period'] ?? '')
      .trim()
      .toLowerCase();
    const parsed = OrderUtils.parseWashCountFrequency(
      packageData['wash_count_frequency'] as number | string | null
    );
    const frequency = parsed !== null && parsed > 0 ? parsed : null;
    if (period && period !== 'unlimited') {
      return { period, frequency };
    }
    return unlimited;
  }

  /**
   * Multi-select checkbox plumbing.
   *
   * `wash_types`, `paired_with`, `features` and `locations` each hold an ARRAY in the
   * form. PrimeNG's `p-checkbox` built that array itself from several boxes sharing one
   * control plus a distinct `[value]`; `mat-checkbox` has no such behaviour — its value
   * accessor is `checked = !!value`, so an array (even an empty one, which is truthy in
   * JS) rendered every box permanently checked, and `[value]` only ever reached the DOM
   * as `value="[object Object]"`. These helpers drive the arrays explicitly instead.
   */
  private getSelection(controlName: string): any[] {
    const value = this.packageForm.get(controlName)?.value;
    return Array.isArray(value) ? value : [];
  }

  private toggleSelection(
    controlName: string,
    item: any,
    checked: boolean,
    isSame: (a: any, b: any) => boolean
  ): void {
    const control = this.packageForm.get(controlName);
    if (!control) return;

    const current = this.getSelection(controlName);
    const alreadyThere = current.some(entry => isSame(entry, item));
    let next = current;
    if (checked && !alreadyThere) {
      next = [...current, item];
    } else if (!checked && alreadyThere) {
      next = current.filter(entry => !isSame(entry, item));
    }

    control.setValue(next);
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  private static sameString(a: any, b: any): boolean {
    return a === b;
  }

  /** Features are objects `{ name, value }`; match on name so a re-mapped object still matches. */
  private static sameFeature(a: any, b: any): boolean {
    return (a?.name ?? a) === (b?.name ?? b);
  }

  private static sameLocation(a: any, b: any): boolean {
    return (a?.id ?? a) === (b?.id ?? b);
  }

  isWashTypeSelected(value: string): boolean {
    return this.getSelection('wash_types').some(entry =>
      AddPackageModalComponent.sameString(entry, value)
    );
  }

  onWashTypeToggle(value: string, checked: boolean): void {
    this.toggleSelection('wash_types', value, checked, AddPackageModalComponent.sameString);
  }

  isPairedWithSelected(value: string): boolean {
    return this.getSelection('paired_with').some(entry =>
      AddPackageModalComponent.sameString(entry, value)
    );
  }

  onPairedWithToggle(value: string, checked: boolean): void {
    this.toggleSelection('paired_with', value, checked, AddPackageModalComponent.sameString);
  }

  isFeatureSelected(feature: any): boolean {
    return this.getSelection('features').some(entry =>
      AddPackageModalComponent.sameFeature(entry, feature)
    );
  }

  onFeatureToggle(feature: any, checked: boolean): void {
    this.toggleSelection('features', feature, checked, AddPackageModalComponent.sameFeature);
  }

  isLocationSelected(location: any): boolean {
    return this.getSelection('locations').some(entry =>
      AddPackageModalComponent.sameLocation(entry, location)
    );
  }

  onLocationToggle(location: any, checked: boolean): void {
    this.toggleSelection('locations', location, checked, AddPackageModalComponent.sameLocation);
  }

  getFilteredFeatures(): any[] {
    if (!this.searchTerm.trim()) {
      return this.features;
    }
    return this.features.filter(feature =>
      feature.name.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  getVisibleFeatures(): any[] {
    const filteredFeatures = this.getFilteredFeatures();
    return this.showAllFeatures ? filteredFeatures : filteredFeatures.slice(0, 10);
  }

  toggleShowAllFeatures(): void {
    this.showAllFeatures = !this.showAllFeatures;
  }

  shouldShowToggleButton(): boolean {
    const filteredFeatures = this.getFilteredFeatures();
    return filteredFeatures.length > 10;
  }

  onSearchFeatures(event: any): void {
    this.searchTerm = event.target.value;
    // Reset to show limited view when searching
    if (this.searchTerm.trim()) {
      this.showAllFeatures = false;
    }
  }

  onWashCountFrequencyInput(event: any): void {
    let value = event.target.value;
    // Remove all non-numeric characters
    value = value.replace(/[^0-9]/g, '');

    // Limit to 3 digits
    const limitedValue = value.slice(0, 3);

    // Update the input value and form control
    if (event.target.value !== limitedValue) {
      event.target.value = limitedValue;
      this.packageForm
        .get('wash_count_frequency')
        ?.setValue(limitedValue ? parseInt(limitedValue, 10) : null, { emitEvent: false });
    }
    // Trigger validation
    this.packageForm.get('wash_count_frequency')?.updateValueAndValidity({ emitEvent: false });
  }

  onListPriceInput(event: Event, controlName: string): void {
    this.applySanitizedPriceToFormControl(event, controlName, { blockZero: false });
  }

  onStrikeoffPriceInput(event: Event, controlName: string): void {
    this.applySanitizedPriceToFormControl(event, controlName, { blockZero: true });
  }

  /**
   * Shared path for root form list/strike inputs.
   * Preserves prior behavior: setValue only when the DOM value changes; always re-validate.
   */
  private applySanitizedPriceToFormControl(
    event: Event,
    controlName: string,
    options: { blockZero: boolean }
  ): void {
    const target = event.target as HTMLInputElement;
    const control = this.packageForm.get(controlName);
    const value = sanitizePriceInputValue(target.value, options);
    if (value === null) {
      target.value = '';
      control?.setValue('', { emitEvent: false });
      control?.updateValueAndValidity({ emitEvent: false });
      return;
    }
    if (target.value !== value) {
      target.value = value;
      control?.setValue(value, { emitEvent: false });
    }
    control?.updateValueAndValidity({ emitEvent: false });
  }

  getFilteredCount(): number {
    return this.getFilteredFeatures().length;
  }

  closeModal() {
    this.submitted = false; // Reset submitted flag when modal closes
    this.hasFormChanges = false; // Reset form changes flag when modal closes
    this.showAllFeatures = false; // Reset features visibility when modal closes
    this.searchTerm = ''; // Reset search term when modal closes
    this.isSubmitting = false; // Reset submitting flag when modal closes
    this.close.emit();
  }

  clearForm() {
    this.submitted = false; // Reset submitted flag when form is cleared
    this.hasFormChanges = false; // Reset form changes flag
    this.showAllFeatures = false; // Reset features visibility when form is cleared
    this.searchTerm = ''; // Reset search term when form is cleared
    this.tunnelSelections = {};
    this.packageForm.reset();
    this.variants.clear();
    this.variantSavedStates = [];
    this.variantKebabOpen = [];
    this.variants.clearValidators();
    this.variants.updateValueAndValidity({ emitEvent: false });
    // Reset to default values
    this.packageForm.patchValue({
      package_status: true,
      package_type: 'car_wash',
      wash_types: ['single_wash'],
      wash_count_period: 'unlimited',
      wash_count_frequency: null,
      family_eligible: 'no',
      license_plate_needed: 'no',
      theme_color: DEFAULT_THEME_BACKGROUND,
      paired_with: [],
      allows_custom_price: false,
    });
    this.variants.disable({ emitEvent: false });
    this.applyAddOnPairedValidators();
    // Mark form as pristine after setting default values
    this.packageForm.markAsPristine();
  }

  savePackage() {
    this.submitted = true; // Set submitted flag to true
    const isUpdate = this.packageData && !this.packageData.isDuplicate;
    if (this.packageForm.get('package_type')?.value === 'add_on') {
      this.variants.controls.forEach(c => {
        const fg = c as FormGroup;
        fg.get('single_list_price')?.updateValueAndValidity({ emitEvent: false });
        fg.get('single_strikeoff_price')?.updateValueAndValidity({ emitEvent: false });
        fg.updateValueAndValidity({ emitEvent: false });
      });
    }
    if (this.packageForm.valid) {
      const formValue = this.packageForm.getRawValue() as any;

      if (formValue.package_type === 'add_on') {
        this.saveAddOnPackage(isUpdate, formValue);
        return;
      }

      const washTypesForPayload =
        formValue.package_type === 'car_wash'
          ? this.filterCarWashWashTypes(formValue.wash_types)
          : Array.isArray(formValue.wash_types)
            ? (formValue.wash_types as string[])
            : [];

      // Car wash requires at least one valid Type of Wash. Filtering can drop invalid tokens
      // (e.g. detailing/add_on) and leave []; do not submit that empty payload.
      if (formValue.package_type === 'car_wash' && washTypesForPayload.length === 0) {
        const washTypesCtrl = this.packageForm.get('wash_types');
        washTypesCtrl?.setValue([], { emitEvent: false });
        washTypesCtrl?.updateValueAndValidity({ emitEvent: false });
        this.markFormGroupTouched(this.packageForm);
        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please fill in all required fields correctly',
        });
        return;
      }

      const hasSingleWash = washTypesForPayload.includes('single_wash');
      const hasMonthlyMembership = washTypesForPayload.includes('monthly_membership');

      const parsePrice = (raw: any): number | null => {
        if (raw === '' || raw === null || raw === undefined) {
          return null;
        }
        const n = parseFloat(String(raw).replace(/[$,]/g, ''));
        return Number.isFinite(n) ? n : null;
      };

      const parseOptionalStrikeoffPrice = (raw: any): number | null => {
        if (this.isEmptyStrikeoffPrice(raw)) {
          return null;
        }
        const n = parseFloat(String(raw).replace(/[$,]/g, ''));
        return Number.isFinite(n) && n > 0 ? n : null;
      };

      const payload: any = {
        ...formValue,
        family_eligible: buildFamilyEligible(
          formValue.family_eligible,
          formValue.package_type,
          formValue.wash_types,
          { featureOn: this.familyPlanFeatureOn, isUpdate: !!isUpdate }
        ),
        license_plate_needed: formValue.license_plate_needed === 'yes',
        locations: formValue.locations.map((loc: any) => loc.id),
        features: formValue.features.map((feat: any) => feat.name),
        type: formValue.package_type,
      };

      const includeMembershipWashCount =
        formValue.package_type === 'car_wash' && hasMonthlyMembership;
      if (includeMembershipWashCount) {
        payload.wash_count_period = formValue.wash_count_period || 'unlimited';
        payload.wash_count_frequency =
          formValue.wash_count_period === 'unlimited'
            ? null
            : formValue.wash_count_frequency
              ? parseInt(formValue.wash_count_frequency, 10)
              : null;
      } else {
        delete payload.wash_count_period;
        delete payload.wash_count_frequency;
      }

      if (formValue.package_type === 'car_wash') {
        // Only send prices for wash types the user selected (avoids `0` from patched form / string `"0"`).
        // If "Custom price" is enabled, omit list/strikeoff prices.
        payload.wash_types = washTypesForPayload;
        const customPrice = this.isAllowsCustomPriceValue(formValue.allows_custom_price);
        payload.single_list_price =
          hasSingleWash && !customPrice ? parsePrice(formValue.single_list_price) : null;
        payload.single_strikeoff_price =
          hasSingleWash && !customPrice
            ? parseOptionalStrikeoffPrice(formValue.single_strikeoff_price)
            : null;
        payload.monthly_list_price = hasMonthlyMembership
          ? parsePrice(formValue.monthly_list_price)
          : null;
        payload.monthly_strikeoff_price = hasMonthlyMembership
          ? parseOptionalStrikeoffPrice(formValue.monthly_strikeoff_price)
          : null;
        payload.allows_custom_price = hasSingleWash ? customPrice : false;
      } else if (formValue.package_type === 'detailing') {
        const customPrice = this.isAllowsCustomPriceValue(formValue.allows_custom_price);
        payload.single_list_price = !customPrice ? parsePrice(formValue.single_list_price) : null;
        payload.single_strikeoff_price = !customPrice
          ? parseOptionalStrikeoffPrice(formValue.single_strikeoff_price)
          : null;
        payload.monthly_list_price = null;
        payload.monthly_strikeoff_price = null;
        payload.allows_custom_price = customPrice;
      }
      delete payload.paired_with;
      delete payload.is_defender;
      delete payload.variants;
      delete payload.package_type;
      if (!isUpdate) {
        payload.type = formValue.package_type;
      }

      // Membership unlimited: keep wash_count_frequency as explicit null so update
      // clears a stale cap. Detailing / single-wash omit both fields entirely.
      const keysToKeepAsNull = new Set<string>();
      if (includeMembershipWashCount && payload.wash_count_frequency === null) {
        keysToKeepAsNull.add('wash_count_frequency');
      }
      for (const key in payload) {
        if (payload[key] === null && !keysToKeepAsNull.has(key)) {
          delete payload[key];
        }
      }

      // CWS-1921: only car wash packages, only locations that show the dropdown.
      if (formValue.package_type === 'car_wash' && this.tunnelWashFields.length) {
        payload.tunnel_packages = buildTunnelPackagesPayload(
          this.tunnelWashFields,
          this.tunnelSelections
        );
      }

      this.subscribePackageSave(!!(this.packageData && !this.packageData.isDuplicate), payload);
    } else {
      this.markFormGroupTouched(this.packageForm);
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly',
      });
    }
  }

  private saveAddOnPackage(isUpdate: boolean, formValue: any): void {
    const variantRows = this.getAddOnVariantRowsForSave();
    const isDuplicateFlow = !!(this.packageData as { isDuplicate?: boolean } | null)?.isDuplicate;
    // Duplicate → new package via add: only send variants the user left checked (saved-row checkbox).
    const variantRowsForPayload =
      isUpdate || !isDuplicateFlow
        ? variantRows
        : variantRows.filter(row => row.is_active !== false);
    if (isDuplicateFlow && variantRows.length > 0 && variantRowsForPayload.length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Select at least one variant to include in the duplicated package.',
      });
      return;
    }
    const pkgName = String(formValue.package_name || '').trim();
    const variantPayload = variantRowsForPayload.map(row => {
      const v = this.mapAddOnVariantRowToPayload(row);
      const n = (v.variant_name != null ? String(v.variant_name) : '').trim();
      if (!n) {
        v.variant_name = pkgName || 'Default';
      }
      return v;
    });
    const first = variantPayload[0] as
      | {
          single_list_price?: number | null;
          single_strikeoff_price?: number | null;
          allows_custom_price?: boolean;
        }
      | undefined;

    // Determine whether the user explicitly added variants.
    // If there's only the single default pricing-only row (no explicit variant name
    // entered by the user), treat it as a normal add-on without variants.
    const isDefaultRowOnly = this.isAddOnDefaultPricingOnlyRow(0);
    const firstRawName =
      variantRows.length === 1 ? String(variantRows[0]?.variant_name ?? '').trim() : '';
    // Also check if any variant already has an API id (was previously saved)
    const hasPersistedVariant = variantRows.some(
      row => row.id != null && String(row.id).trim() !== ''
    );
    const hasExplicitVariants =
      hasPersistedVariant ||
      variantRows.length > 1 ||
      (variantRows.length === 1 &&
        !isDefaultRowOnly &&
        firstRawName !== '' &&
        firstRawName !== pkgName);

    const payload: any = {
      type: 'add_on',
      package_name: formValue.package_name,
      package_status: formValue.package_status,
      paired_with: formValue.paired_with || [],
      features: (formValue.features || []).map((f: { name: string }) => f.name),
      is_defender: false,
      locations: (formValue.locations || []).map((l: { id: string }) => l.id),
      license_plate_needed: formValue.license_plate_needed === 'yes',
      theme_color: formValue.theme_color,
    };

    // Only include variants in the payload when the user explicitly added them.
    if (hasExplicitVariants) {
      payload.variants = variantPayload;
    }

    // Mirror first variant on the root for list/strike columns.
    // When `variants` is omitted (default pricing-only row), API still needs `allows_custom_price`
    // so an empty list price with “Custom price” is valid.
    if (first) {
      const firstAllowsCustom = !!first.allows_custom_price;
      if (!hasExplicitVariants) {
        payload.allows_custom_price = firstAllowsCustom;
      }
      if (firstAllowsCustom && !hasExplicitVariants) {
        // Create API treats omitted list price as undefined → NaN when allows_custom_price is true.
        // Mirror entered variant prices; default to 0 when custom price has no list value.
        const firstRow = variantRows[0];
        const { str: listStr, num: listNum } = this.parseAddOnPriceField(
          firstRow?.single_list_price
        );
        payload.single_list_price = listStr !== '' && !isNaN(listNum) ? listNum : 0;
        const { str: strikeStr, num: strikeNum } = this.parseAddOnPriceField(
          firstRow?.single_strikeoff_price
        );
        if (strikeStr !== '' && !isNaN(strikeNum) && strikeNum > 0) {
          payload.single_strikeoff_price = strikeNum;
        }
      } else if (
        !firstAllowsCustom &&
        first.single_list_price != null &&
        !Number.isNaN(first.single_list_price as number)
      ) {
        payload.single_list_price = String(first.single_list_price);
      }
      if (
        !firstAllowsCustom &&
        first.single_strikeoff_price != null &&
        !Number.isNaN(first.single_strikeoff_price as number) &&
        Number(first.single_strikeoff_price) > 0
      ) {
        payload.single_strikeoff_price = String(first.single_strikeoff_price);
      }
    }
    for (const key of Object.keys(payload)) {
      if (payload[key] === null) {
        delete payload[key];
      }
    }
    this.subscribePackageSave(isUpdate, payload);
  }

  /**
   * Shared create/update subscribe + toast + reload/close. Preserves prior success copy and error sanitization.
   */
  private subscribePackageSave(isUpdate: boolean, payload: object): void {
    this.isSubmitting = true;
    const request$ =
      isUpdate && this.packageData?.id
        ? this.packageService.updatePackage(this.packageData.id, payload)
        : this.packageService.addPackage(payload as PackagePayload);

    request$.subscribe({
      next: () => this.onPackageSaveSuccess(isUpdate),
      error: (err: { error?: { message?: string } }) => this.onPackageSaveError(err),
    });
  }

  private onPackageSaveSuccess(isUpdate: boolean): void {
    this.isSubmitting = false;
    const packageName = this.packageForm.get('package_name')?.value;
    this.messageService.add({
      severity: 'success',
      summary: isUpdate ? 'Package details updated!' : 'New package added!',
      detail: isUpdate
        ? '"' + packageName + '" package has been updated successfully.'
        : '"' + packageName + '" package has been added successfully.',
    });
    this.packageService.loadPackages();
    this.closeModal();
  }

  private onPackageSaveError(err: { error?: { message?: string } }): void {
    this.isSubmitting = false;
    const errorMessage = sanitizeErrorMessage(err.error?.message);
    this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
  }

  /**
   * Marks all fields in a FormGroup or FormArray as touched to trigger validation display
   */
  private markFormGroupTouched(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (!control) {
        return;
      }
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(c => {
          if (c instanceof FormGroup) {
            this.markFormGroupTouched(c);
          } else {
            c.markAsTouched();
          }
        });
      }
    });
  }

  createLocation() {
    this.closeModal();
    this.router.navigate(['/profile'], { queryParams: { tab: 'Location Management' } });
  }
}
